// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Loading from 'react-loading';
import { toast } from 'react-toastify';
import { withRouter } from 'react-router-dom';
import ReCAPTCHA from "react-google-recaptcha";

import api from '../api';
import Checkbox from './Checkbox';
import { getDisplayCardNumber, getCardImage } from '../utilities/formatDisplay';
import { isValidPaymentMethod } from '../utilities/paymentCheck';
import passwordStrength from '../utilities/passwordStrength';
import { CAPTCHA_V2_KEY } from '../data/keys';
import '../styles/start.css';

class GetStarted extends React.Component {

    state = {

        // Animation
        stage: 0,
        visible: false,

        // Submit variables
        plans: 1000,
        name: "",
        email: "",
        team: "",
        password: "",
        plan: "",
        onboarding: "", // Onboarding _id so we know what to update. 
        redirectCode: "", // Redirect code for onboarding auth.
        source: "",
        card: {
            number: "",
            expiry: "",
            cvc: ""
        },
        zip: "",
        country: "US",
        verifyingCard: false,
        check: {
            saas: false,
            tos: false
        },
        deal: "",

        // Validity check
        teamCheck: {
            available: false,
            checking: false,
            valid: false
        },
        emailCheck: {
            exists: false,
            checking: false,
            valid: false,
        },
        cardCheck: {
            number: true,
            expiry: true,
            cvc: true,
            zip: true,
            country: true
        }
    }

    roi = () => `https://x.stax.ai/roi?name=${this.state.name}&plans=${this.state.plans}`

    componentDidMount = () => {
        this.captcha = React.createRef();

        // Plan selected?
        const query = new URLSearchParams(this.props.location.search);
        const plan = query.get('plan');
        if (plan) this.setState({ plan });
        
        const coupon = query.get('coupon');
        if (coupon) this.setState({ coupon });
       
        const source = query.get('source');
        if (source) this.setState({ source });

        // Also allow passing email and name to pre-fill
        const email = query.get('email');
        if (email) this.setState({ email });

        const name = query.get('name');
        if (name) this.setState({ name });

        const team = query.get('team');
        if (team) 
        {
            this.setState({ team });
            this.checkTeamNameAvailability(team);
        }

        const plans = query.get('plans');
        if (plans) this.setState({ plans: parseInt(plans ?? "1000") });

        const onboarding = query.get('onboarding');
        if (onboarding) this.setState({ onboarding });

        // return this.nextStage(null, 6); // For testing only!

        setTimeout(() => this.setState({
            stage: 1,
            visible: true
        }), 500);
        setTimeout(() => {
            if (email?.length && name?.length && team?.length) {
                this.nextStage(null, 4);
                setTimeout(() => this.nextStage(null, 6), 3000);
                return;
            }
            if (email?.length && name?.length) {
                this.nextStage(null, 4);
                setTimeout(() => this.nextStage(null, 5), 3000);
                return;
            }
            this.nextStage(null, email?.length ? 3 : 2);
        }, 2000);
    }

    nextStage = (e, stage) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        this.setState({ visible: false });
        setTimeout(() => this.setState({
            stage: stage ? stage : this.state.stage+1,
            visible: true
        }), 700);
    }

    checkTeamNameAvailability = async (name) => {
        if (this.teamNameCheckTimeout)
            clearTimeout(this.teamNameCheckTimeout); // Block previous search

        if (!name || name.length <= 9)
            return this.setState({
                teamCheck: {
                    available: false,
                    checking: false
                }
            });

        await this.setState({
            teamCheck: {
                checking: true,
                available: false,
                valid: true
            }
        });

        this.teamNameCheckTimeout = setTimeout(async () => {
            const res = await api.get('/team/available?name=' + name);
            this.setState({
                teamCheck: {
                    available: res.available || this.state.onboarding,
                    checking: false,
                    valid: true
                }
            });
        }, 500);
    }

    checkEmailAvailability = async (email) => {
        if (this.emailCheckTimeout)
            clearTimeout(this.emailCheckTimeout); // Block previous search

        if (!email || email.length <= 8 || !email.includes('@'))
            return this.setState({
                emailCheck: {
                    exists: false,
                    checking: false,
                    valid: false
                }
            });

        await this.setState({
            emailCheck: {
                exists: false,
                checking: true,
                valid: true
            }
        });

        this.emailCheckTimeout = setTimeout(async () => {
            const res = await api.get('/user/exists?email=' + encodeURIComponent(email));
            if(res?.user?.sso?.enabled)
                toast.error("You cannot create a new team with an account already tied to an organization.")

            this.setState({
                emailCheck: {
                    exists: res?.exists,
                    checking: false,
                    valid: !res?.user?.sso?.enabled
                },
                name: res?.user?.name ?? ""
            });
        }, 500);
    }

    createAccount = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        this.nextStage(null, 8);

        // Create account and auto-login
        const { name, email, password, plan, source, coupon, deal, onboarding } = this.state;
        const team = plan === "Personal" ? name + " (Personal)" : this.state.team;
        const res = await api.post('/team/create', { name, email, password, team, plan, source, coupon, deal, onboarding });

        const code = res?.code; 
        this.setState({ redirectCode: code });

        if (!res || !res.success)
            return this.nextStage(null, 7); // Return to try again

        // If successful, the API sets user cookies, we can go forward to login! :)
        setTimeout(() => this.nextStage(null, 9), 1000);
    }

    setPaymentMethod = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        this.setState({ verifyingCard: true });

        const { card, zip, country } = this.state;
        const ok = isValidPaymentMethod(card, zip, country);
        this.setState({ cardCheck: ok.card });

        if (!ok.all) {
            this.setState({ verifyingCard: false });
            toast.error("Please fix the issues below.");
            return;
        }

        const res = await api.post('/team/billing/method/create', {
            type: "card",
            method: {
                card: {
                    number: card.number,
                    exp_month: card.expiry.substr(0,2),
                    exp_year: card.expiry.substr(3),
                    cvc: card.cvc
                },
                billing_details: {
                    address: {
                        line1: "",
                        line2: "",
                        postal_code: card.zip,
                        state: "",
                        country: card.country
                    },
                    name: this.state.name,
                    email: this.state.email
                }
            }
        });

        this.setState({ verifyingCard: false });

        if (!res || !res.success) return;

        const params = new URLSearchParams(window.location.search); 
        // Check if onboarding is in the params... 
        if (params.get('onboarding')){
            // We need to redirect to setup, get the full current url (For example, https://my.stax.ai)
            const currentUrl = window.location.origin; 
            // Replace my, with setup. 


            const setupUrl = currentUrl.includes("localhost")? "http://localhost:3001": currentUrl.replace('my', 'setup'); 
            // Redirect to the setup url.
            window.location.href = `${setupUrl}?code=${this.state.redirectCode}`;
        }

        this.nextStage();
        setTimeout(async () => {
            await this.props.whoAmI();
            this.props.history.push('/');
        }, 10000);
    }

    renderHint = (check, available, taken) => {
        return (<div className="start-content-hint">
            {check.valid && !check.checking ? (check.available ? <>
                <img
                    src="https://static.stax.ai/images/icons/reviewed.png"
                    alt="Available"
                />{available}
            </> : <>
                <img
                    src="https://static.stax.ai/images/icons/alert.png"
                    alt="Unavailable"
                />{taken}
            </>) : ""}
        </div>);
    }

    render = () => {
        document.title = "Welcome to Stax·ai!";

        const pass = passwordStrength(this.state.password);
        return (<div className="start-container">
            <div className={"start-content" + (!this.state.visible ? " start-content-hidden" : "")}>

                {/* Hi */}
                {this.state.stage === 1 ? <>
                    <h1>Hi!</h1>
                </> :
                
                // Email address
                this.state.stage === 2 ? <>
                    <h1>What's your email address?</h1>
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        // Check if email is valid before proceeding to next step
                        if (!this.state.emailCheck?.valid)
                            return toast.error("Please enter a valid email address.")

                        if (this.state.email && this.state.email.length <= 9)
                            return toast.error("Email address must be at least 10 characters long.");
                        if (this.state.emailCheck.checking)
                            return;
                        

                        this.nextStage(null, this.state.name.length ? 4 : 3);
                        if (this.state.name.length)
                            setTimeout(() => this.nextStage(null, this.state.onboarding? 7: null), 4000);
                    }}>
                        <input
                            ref="email"
                            id="email"
                            name="email"
                            type="text"
                            placeholder="Enter email here..."
                            value={this.state.email}
                            onChange={async (e) => {
                                await this.setState({ email: e.target.value });
                                await this.checkEmailAvailability(this.state.email);
                            }}
                            autoComplete="off"
                            autoFocus
                        />
                        {this.state.emailCheck.checking ? <div className="start-content-checking">
                            <Loading
                                type="spin"
                                color="var(--color-accent)"
                                height={32}
                                width={32}
                            />
                        </div> : this.state.visible && this.state.emailCheck.valid? <input
                            type="submit"
                            value=""
                            title="Next"
                            readOnly={true}
                        /> : ""}
                    </form>
                </> :

                // User name - can be skipped if user already exists
                this.state.stage === 3 ? <>
                    <h1>What's your name?</h1>
                    <form onSubmit={(e) => {
                        this.nextStage(e);
                        setTimeout(() => this.nextStage(null, this.state.onboarding? 7: this.state.plan === "Personal" ? 6 : 5), 4000);
                    }}>
                        <input
                            ref="name"
                            id="name"
                            name="name"
                            type="text"
                            placeholder="Enter full name here..."
                            value={this.state.name}
                            onChange={e => this.setState({ name: e.target.value })}
                            autoComplete="off"
                            autoFocus
                        />
                        {this.state.visible && this.state.name !== "" && <input
                            type="Submit"
                            title="Next"
                            value=""
                            readOnly={true}
                        />}
                    </form>
                </> :
                
                // Welcome
                this.state.stage === 4 ? <>
                    <h1>{this.state.emailCheck.exists ? "Good to see you again," : "It's great to meet you,"} <span className="start-content-underline">{this.state.name.split(" ")[0]}</span>!</h1>
                </> :
                
                // Team name
                this.state.stage === 5 ? <>
                    <h1>{this.state.emailCheck.exists ? "What should we call this team?" : "What company are you with?"}</h1>
                    <form onSubmit={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (this.state.team && this.state.team.length <= 7)
                            return toast.error("Team name must be at least 8 characters long.");
                        if (this.state.teamCheck.checking)
                            return;
                        if (!this.state.teamCheck.available)
                            return toast.error("Please enter another company name.");
                        this.nextStage(null, this.state.plan?.length ? 7 : 6);
                        // Submit the information we have so far as a lead
                        const res = await api.post('/lead/start', {
                            name: this.state.name,
                            email: this.state.email,
                            team: this.state.team,
                            plan: this.state.plan
                        });
                        if (res?.dealId)
                            this.setState({ deal: res.dealId });
                    }}>
                        <input
                            ref="company"
                            id="company"
                            name="company"
                            type="text"
                            placeholder="Enter company name here..."
                            value={this.state.team}
                            onChange={async (e) => {
                                await this.setState({ team: e.target.value });
                                await this.checkTeamNameAvailability(this.state.team);
                            }}
                            autoComplete="off"
                            autoFocus
                        />
                        {this.state.teamCheck.checking ? <div className="start-content-checking">
                            <Loading
                                type="spin"
                                color="var(--color-accent)"
                                height={32}
                                width={32}
                            />    
                        </div> : this.state.visible && this.state.teamCheck.available ? <input
                            type="submit"
                            title="Next"
                            value=""
                            readOnly={true}
                        /> : ""}
                    </form>
                    {this.renderHint(this.state.teamCheck, "Company name is available!", "A company with this name is already on Stax.")}
                </> :

                // Plan selection
                this.state.stage === 6 ? <>
                    <h1 style={{marginBottom: 10}}>Pick the right plan for you</h1>
                    <p style={{paddingBottom: 0}}>How many retirement plans do you administer?</p>
                    <div class="start-num-plans">
                        {this.state.plans <= 150 ? "< 150" : this.state.plans >= 4000 ? "> 4000" : this.state.plans}
                        <span class="start-num-plans-label">plans</span>
                    </div>
                    <form onScroll={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}>
                        <input
                            type="range"
                            min={150}
                            max={4000}
                            value={this.state.plans}
                            onChange={e => this.setState({ plans: e.target.value })}
                        />
                    </form>
                    <div class="start-plan-details">
                        {this.state.plans <= 50 ? <>
                            <h1>Almost there...</h1>
                            <p>
                                Stax.ai is designed to offer the most value to TPAs with a minimum of <strong>50 plans</strong>. As your business continues to grow, consider signing up for our newsletter to receive valuable content that will assist you on your journey.
                            </p>
                            <div
                                className="submit"
                                onClick={async () => {
                                    await api.post('/lead/newsletter', {
                                        email: this.state.email
                                    });
                                    toast.success("You have subscribed to our newsletter. Check your inbox!");
                                }}
                            >Get notified</div>
                        </> :

                        this.state.plans <= 200 ? <>
                        <div class="start-plan-label">Your recommended plan</div>
                        <div class="start-plan-cost">
                            $799
                            <span class="start-plan-cost-sub">per month</span>
                        </div>
                        <p>
                            This plan allows you to scan <strong>18,000</strong> brokerage statement pages per year for trust accounting, download file up to <strong>2,500</strong> reports from our supported record-keepers annually, and provides access to <strong>3</strong> user accounts for your team.
                        </p>
                        <p>
                            View the <a href="/sla/18k?from=start" target="_blank" rel="noreferrer noopener">SaaS Master Services Agreement</a> for full details.
                        </p>
                        <div
                            className="submit"
                            onClick={(e) => {
                                this.setState({ plan: "18k" });
                                this.nextStage(e);
                                api.post('/lead/select', {
                                    tpaPlans: this.state.plans,
                                    dealId: this.state.deal,
                                    staxPlan: "18k"
                                });
                            }}
                        >Select this plan</div>
                        <div class="start-roi">
                            Not seeing the value?<br />Explore the possibilities with our <a href={this.roi()} target="_blank" rel="noreferrer noopener">ROI Calculator</a>.
                        </div>
                        </> :
                        
                        this.state.plans <= 500 ? <>
                            <div class="start-plan-label">Your recommended plan</div>
                            <div class="start-plan-cost">
                                $1,399
                                <span class="start-plan-cost-sub">per month</span>
                            </div>
                            <p>
                                This plan allows you to scan <strong>36,000</strong> brokerage statement pages per year for trust accounting, download file up to <strong>5,000</strong> reports from our supported record-keepers annually, and provides access to <strong>6</strong> user accounts for your team.
                            </p>
                            <p>
                                View the <a href="/sla/36k?from=start" target="_blank" rel="noreferrer noopener">SaaS Master Services Agreement</a> for full details.
                            </p>
                            <div
                                className="submit"
                                onClick={(e) => {
                                    this.setState({ plan: "36k" });
                                    this.nextStage(e);
                                    api.post('/lead/select', {
                                        tpaPlans: this.state.plans,
                                        dealId: this.state.deal,
                                        staxPlan: "36k"
                                    });
                                }}
                            >Select this plan</div>
                            <div class="start-roi">
                                Not seeing the value?<br />Explore the possibilities with our <a href={this.roi()} target="_blank" rel="noreferrer noopener">ROI Calculator</a>.
                            </div>
                        </> :

                        this.state.plans <= 1200 ? <>
                            <div class="start-plan-label">Your recommended plan</div>
                            <div class="start-plan-cost">
                                $2,399
                                <span class="start-plan-cost-sub">per month</span>
                            </div>
                            <p>
                            This plan allows you to scan <strong>72,000</strong> brokerage statement pages per year for trust accounting, download file up to <strong>10,000</strong> reports from our supported record-keepers annually, and provides access to <strong>10</strong> user accounts for your team.
                            </p>
                            <p>
                                View the <a href="/sla/72k?from=start" target="_blank" rel="noreferrer noopener">SaaS Master Services Agreement</a> for full details.
                            </p>
                            <div
                                className="submit"
                                onClick={(e) => {
                                    this.setState({ plan: "72k" });
                                    this.nextStage(e);
                                    api.post('/lead/select', {
                                        tpaPlans: this.state.plans,
                                        dealId: this.state.deal,
                                        staxPlan: "72k"
                                    });
                                }}
                            >Select this plan</div>
                            <div class="start-roi">
                                Not seeing the value?<br />Explore the possibilities with our <a href={this.roi()} target="_blank" rel="noreferrer noopener">ROI Calculator</a>.
                            </div>
                        </> :
                        
                        this.state.plans <= 2200 ? <>
                            <div class="start-plan-label">Your recommended plan</div>
                            <div class="start-plan-cost">
                                $4,299
                                <span class="start-plan-cost-sub">per month</span>
                            </div>
                            <p>
                                This plan allows you to scan <strong>144,000</strong> brokerage statement pages per year for trust accounting, download file up to <strong>20,000</strong> reports from our supported record-keepers annually, and provides access to <strong>15</strong> user accounts for your team.
                            </p>
                            <p>
                                View the <a href="/sla/144k?from=start" target="_blank" rel="noreferrer noopener">SaaS Master Services Agreement</a> for full details.
                            </p>
                            <div
                                className="submit"
                                onClick={(e) => {
                                    this.setState({ plan: "144k" });
                                    this.nextStage(e);
                                    api.post('/lead/select', {
                                        tpaPlans: this.state.plans,
                                        dealId: this.state.deal,
                                        staxPlan: "144k"
                                    });
                                }}
                            >Select this plan</div>
                            <div class="start-roi">
                                Not seeing the value?<br />Explore the possibilities with our <a href={this.roi()} target="_blank" rel="noreferrer noopener">ROI Calculator</a>.
                            </div>
                        </> :
                        
                        this.state.plans < 5000 ? <>
                            <div class="start-plan-label">Your recommended plan</div>
                            <div class="start-plan-cost">
                                $7,799
                                <span class="start-plan-cost-sub">per month</span>
                            </div>
                            <p>
                                This plan allows you to scan <strong>300,000</strong> brokerage statement pages per year for trust accounting, download file up to <strong>50,000</strong> reports from our supported record-keepers annually, and provides access to <strong>30</strong> user accounts for your team.
                            </p>
                            <p>
                                View the <a href="/sla/300k?from=start" target="_blank" rel="noreferrer noopener">SaaS Master Services Agreement</a> for full details.
                            </p>
                            <div
                                className="submit"
                                onClick={(e) => {
                                    this.setState({ plan: "300k" });
                                    this.nextStage(e);
                                    api.post('/lead/select', {
                                        tpaPlans: this.state.plans,
                                        dealId: this.state.deal,
                                        staxPlan: "300k"
                                    });
                                }}
                            >Select this plan</div>
                            <div class="start-roi">
                                Not seeing the value?<br />Explore the possibilities with our <a href={this.roi()} target="_blank" rel="noreferrer noopener">ROI Calculator</a>.
                            </div>
                        </> : <>
                            <div class="start-plan-label">Impressive! You're a market leader.</div>
                            <p>
                                For TPAs managing more than 5,000 plans, we offer custom pricing with volume discounts. Please schedule a call with our sales team to receive a proposal tailored to the unique needs of larger TPAs.
                            </p>
                            <div
                                className="submit"
                                onClick={() => window.open("https://calendly.com/staxai/live-demo", "_blank")}
                            >Schedule a call</div>
                        </>}
                    </div>
                </> :
                
                // Password
                this.state.stage === 7 ? <>
                    <h1>{this.state.emailCheck.exists ? "Enter your login password" : "Create a login password"}</h1>
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}>
                        <input
                            ref="password"
                            id="password"
                            name="password"
                            type="password"
                            placeholder="Enter a secure password..."
                            value={this.state.password}
                            onChange={e => this.setState({ password: e.target.value })}
                            autoComplete="off"
                            autoFocus
                        />
                        <div className="start-content-hint">
                            {this.state.password !== "" ? (pass === "ok" ? <>
                                <img
                                    src="https://static.stax.ai/images/icons/reviewed.png"
                                    alt="Available"
                                />Awesome, that's a strong password!
                            </> : <>
                                <img
                                    src="https://static.stax.ai/images/icons/alert.png"
                                    alt="Unavailable"
                                />{pass}
                            </>) : ""}
                        </div>
                        <Checkbox
                            className="start-checkbox"
                            label={<>I have read and hereby agree to the <a href={`/sla/${this.state.plan}`} target="_blank" rel="noopener noreferrer">Master Service Agreement</a>.</>}
                            checked={this.state.check?.saas}
                            onToggle={() => this.setState({ check: { ...this.state.check, saas: !this.state.check?.saas ? true : false } })}
                        />
                        <Checkbox
                            className="start-checkbox"
                            label={<>I have read and hereby agree to the <a href={`https://stax.ai/license`} target="_blank" rel="noopener noreferrer">Stax·ai Terms of Service</a>.</>}
                            checked={this.state.check?.tos}
                            onToggle={() => this.setState({ check: { ...this.state.check, tos: !this.state.check?.tos ? true : false } })}
                        />
                        {this.state.visible && pass === "ok" && this.state.check?.saas && this.state.check?.tos && <div
                            className="submit start-submit"
                            onClick={() => {
                                if (this.state.password === "")
                                    return;
                                if (pass !== "ok")
                                    return toast.error("Your password is not strong enough.");
                                this.captcha.current.execute();
                            }}
                        >Create account</div>}
                        <ReCAPTCHA
                            ref={this.captcha}
                            sitekey={CAPTCHA_V2_KEY}
                            onChange={(value) => {
                                if (!value)
                                    return;
                                this.createAccount();
                            }}
                            size="invisible"
                        />
                    </form>
                </> :

                // Creating...
                this.state.stage === 8 ? <>
                    <h1>Creating your account...</h1>
                </> :

                // Credit card information
                this.state.stage === 9 ? <>
                    <h1>Set up a payment method</h1>
                    <div className="start-content-line" />
                    <p>
                        You need to attach a credit card to your account. Your credit card will be charged as per the terms laid out on the Master Services Agreement.
                    </p>
                    <form className="start-payment-form" onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}>
                        <label>Credit Card</label>
                        <div className="start-payment-input-inline">
                            <input
                                ref="card"
                                className={!this.state.cardCheck.number ? "input-error" : ""} 
                                type="text"
                                maxLength="19"
                                placeholder="XXXX XXXX XXXX XXXX"
                                autoComplete="off"
                                value={getDisplayCardNumber(this.state.card.number)}
                                autoFocus
                                onChange={(e) => this.setState({
                                    card: {
                                        ...this.state.card,
                                        number: e.target.value.replace(/\s/g, ''),
                                    },
                                    cardCheck: {
                                        ...this.state.cardCheck,
                                        number: true
                                    }
                                })}
                            />

                            {this.state.cardCheck.number ? <div className="form-input-logo">{getCardImage(this.state.card.number)}</div> : ""}
                        </div>
                        <div style={{ clear: 'both' }} />

                        <label>Expiry Date</label>
                        <div
                            className="start-payment-input-inline"
                            style={{ width: 140 }}
                        >
                            <input
                                className={!this.state.cardCheck.expiry ? "input-error" : ""}
                                type="text" placeholder="MM/YYYY"
                                autoComplete="off" value={this.state.card.expiry}
                                onChange={(e) => {
                                    let expiry = e.target.value;

                                    // Erasing?
                                    if (this.state.card.expiry.length === 3 && expiry.length === 2)
                                        return this.setState({ card: { ...this.state.card, expiry } });

                                    if (expiry.length > 2) {
                                        if (expiry[2] !== "/")
                                            expiry = expiry.substr(0,2) + "/" + expiry.substr(2);
                                        if (isNaN(expiry.substr(0,2)) || isNaN(expiry.substr(3)))
                                            return;
                                    }
                                    else if (expiry.length === 2) expiry += "/";
                                    else if (isNaN(expiry)) return;

                                    this.setState({
                                        card: {
                                            ...this.state.card,
                                            expiry
                                        },
                                        cardCheck: {
                                            ...this.state.cardCheck,
                                            expiry: true
                                        }
                                    });
                                }}
                            />
                        </div>

                        <label style={{ width: 38, marginLeft: 30 }}>CVC</label>
                        <div
                            className="start-payment-input-inline"
                            style={{ width: 100 }}
                        >
                            <input
                                className={!this.state.cardCheck.cvc ? "input-error" : ""}
                                type="text"
                                placeholder="XXXX"
                                autoComplete="off"
                                value={this.state.card.cvc}
                                onChange={(e) => {
                                    const cvc = e.target.value;
                                    if (isNaN(cvc)) return;
                                    this.setState({
                                        card: {
                                            ...this.state.card,
                                            cvc
                                        },
                                        cardCheck: {
                                            ...this.state.cardCheck,
                                            cvc: true
                                        }
                                    });
                                }}
                            />
                        </div>

                        <div style={{ clear: 'both' }} />

                        <label>ZIP Code</label>
                        <div
                            className="start-payment-input-inline"
                            style={{ width: 110 }}
                        >
                            <input
                                className={!this.state.cardCheck.zip ? "input-error" : ""}
                                type="text"
                                placeholder="XXXXX"
                                autoComplete="off"
                                value={this.state.zip}
                                onChange={(e) => this.setState({
                                    zip: e.target.value,
                                    cardCheck: {
                                        ...this.state.cardCheck,
                                        zip: true
                                    }
                                })}
                            />
                        </div>

                        <label style={{ width: 70, marginLeft: 48 }}>Country</label>
                        <div
                            className="start-payment-input-inline"
                            style={{ width: 80 }}
                        >
                            <input
                                className={!this.state.cardCheck.country ? "input-error" : ""}
                                type="text"
                                placeholder="XX"
                                autoComplete="off"
                                value={this.state.country}
                                onChange={(e) => this.setState({
                                    country: e.target.value,
                                    cardCheck: {
                                        ...this.state.cardCheck,
                                        country: true
                                    }
                                })}
                            />
                        </div>

                        <div style={{ clear: 'both', height: 20 }} />

                        {this.state.verifyingCard ? <div
                            className="start-submit-verifying"
                        >
                            <Loading
                                type="bubbles"
                                color="var(--color-primary)"
                                height={80}
                                width={80}
                            />
                        </div> : <div
                            className="start-submit-button"
                            onClick={this.setPaymentMethod}
                        >Add Credit Card</div>}
                    </form>
                </> :
                
                // Account created!
                this.state.stage === 10 ? <>
                    <h1>Let's get you automating!</h1>
                    <div className="start-content-line" />
                    <h2>Welcome to Stax<div className="navigation-logo-dot"></div>ai!</h2>
                    <p>
                        Our <strong>Customer Success Team</strong> will reach out to you shortly to schedule an onboarding call during which we will sync your existing platforms, set up your plans, and configure your customized workflow automations.
                        <br />
                        We've very excited and grateful that you've joined our family!
                    </p>
                </> : ""}

            </div>
        </div>);
    }

};

export default withRouter(GetStarted);